
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { devLog, devError } from '@/utils/devLog';

// Define the StripeAccountStatus interface for type safety
export interface StripeAccountStatus {
  connected: boolean;
  stripeAccountId?: string;
  onboardingComplete?: boolean;
  detailsSubmitted?: boolean;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
}

export async function createCourseEnrollmentCheckout(
  courseId: string,
  userId: string,
  price: number | string,
  courseName: string,
  username?: string,
  courseSlug?: string
): Promise<string | null> {
  if (!userId) {
    devError('[ENROLLMENT] Cannot create checkout: User not authenticated');
    return null;
  }

  try {
    // Price is already in dollars, we'll let the Stripe function handle conversion to cents
    // Make sure price is a valid number
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    
    if (isNaN(numericPrice)) {
      devError('[ENROLLMENT] Invalid price value:', { price });
      toast.error('Invalid price value');
      return null;
    }

    devLog('[ENROLLMENT] Creating checkout session for course enrollment', { 
      courseId, 
      userId, 
      priceInDollars: numericPrice,
      courseName,
      username,
      courseSlug,
      timestamp: new Date().toISOString()
    });
    
    const { data, error } = await supabase.functions.invoke('stripe-checkout', {
      method: 'POST',
      body: { 
        courseId, 
        userId,
        price: numericPrice, // Pass the price in dollars, conversion happens in the function
        courseName,
        username,
        courseSlug
      }
    });

    if (error) {
      devError('[ENROLLMENT] Error creating checkout session:', error);
      throw new Error(error.message || 'Failed to create checkout session');
    }

    if (!data?.url) {
      devError('[ENROLLMENT] No checkout session URL returned in response:', data);
      throw new Error('Failed to create checkout session: No URL returned');
    }

    // CRITICAL FIX: Add direct console.log of the URL before returning
    const redirectUrl = data.url;
    console.log('[ENROLLMENT] Received Stripe checkout URL:', redirectUrl);
    console.debug('[Stripe] checkoutUrl', redirectUrl);
    
    // CRITICAL FIX: Verify URL format appears to be valid
    if (!redirectUrl.startsWith('http')) {
      devError('[ENROLLMENT] Invalid checkout URL format:', { url: redirectUrl });
      throw new Error('Invalid checkout URL format received from Stripe');
    }
    
    devLog('[ENROLLMENT] Checkout session created successfully', { 
      sessionUrl: redirectUrl.substring(0, 30) + '...',
      timestamp: new Date().toISOString()
    });
    
    return redirectUrl;
  } catch (error) {
    devError('[ENROLLMENT] Error creating checkout session:', error);
    toast.error('Failed to initiate course enrollment');
    throw error;
  }
}

export async function createStripeAccountLink(userId: string): Promise<string> {
  try {
    devLog('[STRIPE-CONNECT] Creating account link for user', { userId });
    
    const { data, error } = await supabase.functions.invoke('stripe-connect', {
      method: 'POST',
      body: { userId }
    });

    if (error) {
      devError('[STRIPE-CONNECT] Error creating account link:', error);
      throw new Error(error.message || 'Failed to create Stripe account link');
    }

    if (!data?.url) {
      devError('[STRIPE-CONNECT] No account link URL returned in response:', data);
      throw new Error('Failed to create Stripe account link: No URL returned');
    }

    devLog('[STRIPE-CONNECT] Account link created successfully');
    return data.url;
  } catch (error) {
    devError('[STRIPE-CONNECT] Error creating account link:', error);
    toast.error('Failed to create Stripe account link');
    throw error;
  }
}

export async function getStripeConnectAccount(userId: string): Promise<any> {
  try {
    devLog('[STRIPE-CONNECT] Fetching account for user', { userId });
    
    const { data, error } = await supabase
      .from('stripe_connect_accounts')
      .select('*')
      .eq('creator_id', userId)
      .maybeSingle();
    
    if (error) {
      devError('[STRIPE-CONNECT] Error fetching account:', error);
      throw new Error(error.message || 'Failed to fetch Stripe account');
    }
    
    devLog('[STRIPE-CONNECT] Account fetch result', { 
      exists: !!data,
      accountId: data?.stripe_account_id
    });
    
    return data;
  } catch (error) {
    devError('[STRIPE-CONNECT] Error fetching account:', error);
    toast.error('Failed to fetch Stripe account information');
    throw error;
  }
}

export async function getStripeAccountStatus(userId: string): Promise<StripeAccountStatus> {
  try {
    devLog('[STRIPE-CONNECT] Fetching account status for user', { userId });
    
    const { data, error } = await supabase.functions.invoke('stripe-connect/status', {
      method: 'POST',
      body: { userId }
    });

    if (error) {
      devError('[STRIPE-CONNECT] Error fetching account status:', error);
      throw new Error(error.message || 'Failed to fetch Stripe account status');
    }
    
    devLog('[STRIPE-CONNECT] Account status fetch result', data);
    
    return data as StripeAccountStatus;
  } catch (error) {
    devError('[STRIPE-CONNECT] Error fetching account status:', error);
    toast.error('Failed to fetch Stripe account status');
    return { connected: false };
  }
}

export async function disconnectStripeAccount(userId: string): Promise<boolean> {
  try {
    devLog('[STRIPE-CONNECT] Disconnecting account for user', { userId });
    
    const { error } = await supabase.functions.invoke('stripe-connect/disconnect', {
      method: 'DELETE',
      body: { userId }
    });

    if (error) {
      devError('[STRIPE-CONNECT] Error disconnecting account:', error);
      throw new Error(error.message || 'Failed to disconnect Stripe account');
    }
    
    devLog('[STRIPE-CONNECT] Account disconnected successfully');
    return true;
  } catch (error) {
    devError('[STRIPE-CONNECT] Error disconnecting account:', error);
    toast.error('Failed to disconnect Stripe account');
    throw error;
  }
}
